// apps/web/src/pages/admin/today/TodayListView.tsx — Phase 5 plan 05-04 (B2)
// + Quick 260803-ger.
//
// Renders the B2 list view: capacity bar + sort chips + reservation
// rows + bottom-bar CTA. Each row uses StatusTag + SourceTag +
// reservationRowClass. Empty state renders B2·C.
//
// As of 260803-ger, this view is *controlled* by the parent
// Reservations page: the parent owns the date, the sort key, and the
// filtered items. The view no longer fetches its own data or holds its
// own sort state — that fixes the inert sort-chip bug (the chips now
// actually reorder the rows) and the sort state survives a layout
// switch (the parent preserves it across the list/timeline toggle).
//
// The view delegates the actual card markup to a `renderItem` callback
// so the parent can attach the edit + cancel actions to each card
// (per CONTEXT.md: "Preserve the Today card contact rendering ...
// and combine it with ReservationRow edit/cancel actions rather than
// collapsing contact fields").

import { Link } from "react-router-dom";
import { format } from "date-fns";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TodayCapacityBar } from "./TodayCapacityBar";
import { TodaySortChips, type TodaySortKey } from "./TodaySortChips";
import { StatusTag, reservationRowClass } from "../shared/StatusTag";
import { SourceTag } from "../shared/SourceTag";
import type { ReservationStatusValue } from "../shared/StatusTag";
import type { ReservationSourceValue } from "../shared/SourceTag";

export interface TodayReservationItem {
  id: string;
  reservationNumber: string;
  status: ReservationStatusValue;
  source: ReservationSourceValue;
  startsAt: string;
  durationMinutes: number;
  partySize: number;
  guestName: string;
  guestEmail: string | null;
  guestPhone: string | null;
  internalNote: string | null;
  slotDefinitionId: string;
  serviceDate: string;
}

export function TodayListView({
  date,
  items,
  sortKey,
  onSortChange,
  manualCreateHref,
  renderItem,
}: {
  date: string;
  items: TodayReservationItem[];
  sortKey: TodaySortKey;
  onSortChange: (next: TodaySortKey) => void;
  manualCreateHref: string;
  renderItem: (item: TodayReservationItem) => ReactNode;
}) {
  const { t } = useTranslation();
  const sorted = [...items].sort((a, b) => {
    if (sortKey === "time") {
      return a.startsAt.localeCompare(b.startsAt);
    }
    if (sortKey === "name") {
      return a.guestName.localeCompare(b.guestName);
    }
    return b.partySize - a.partySize;
  });

  if (items.length === 0) {
    return (
      <div data-testid="today-empty-state">
        <TodayCapacityBar date={date} />
        <div className="card p-6 text-center">
          <p className="font-heading text-xl mb-4">
            {t("admin.today.emptyState.headline")}
          </p>
          <Link
            to={manualCreateHref}
            className="btn btn-primary"
            data-testid="today-empty-state-cta"
          >
            {t("admin.today.emptyState.ctaLabel")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="today-list">
      <TodayCapacityBar date={date} />
      <TodaySortChips value={sortKey} onChange={onSortChange} />
      <ul className="flex flex-col gap-2" data-testid="today-list-rows">
        {sorted.map((item) => (
          <li
            key={item.id}
            className={`card p-4 flex flex-col gap-2 ${reservationRowClass(item.status)}`}
            data-testid={`today-row-${item.id}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-heading text-lg">
                {format(new Date(item.startsAt), "HH:mm")} ·{" "}
                {item.reservationNumber}
              </span>
              <div className="flex gap-2">
                <StatusTag status={item.status} />
                <SourceTag source={item.source} />
              </div>
            </div>
            <div className="text-sm">
              <span className="font-medium">{item.guestName}</span>
              {/* Render email and phone independently — the prior
                  `??` collapse hid the phone whenever email was set,
                  which blocks the staff "call the guest" workflow
                  for the most common case (online booking). */}
              {item.guestEmail && (
                <span className="text-text/60"> · {item.guestEmail}</span>
              )}
              {item.guestPhone && (
                <span className="text-text/60"> · {item.guestPhone}</span>
              )}
              {!item.guestEmail && !item.guestPhone && (
                <span className="text-text/60"> · —</span>
              )}
              <span className="text-text/60"> · {item.partySize} Pers.</span>
            </div>
            {item.internalNote && (
              <p className="text-xs text-text/60 italic">{item.internalNote}</p>
            )}
            {renderItem(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}
